/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ButtonLayout } from '../UNSAFE_ButtonLayout';

import { ChevronDownIcon } from '../PRIVATE_ThemedIcons/ChevronDownIcon';

import { Menu } from '../UNSAFE_Menu';
import { useMenuAction, MenuToggleDetail } from '../hooks/UNSAFE_useMenuAction';
import { useTooltip } from '../hooks/UNSAFE_useTooltip';

import { BaseButton } from '../UNSAFE_BaseButton';

import type { DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';

import { ComponentChildren, ComponentChild, Fragment } from 'preact';
import { forwardRef, ForwardedRef } from 'preact/compat';
import { useRef, useImperativeHandle } from 'preact/hooks';
import { mergeProps } from '../utils/UNSAFE_mergeProps';

type WidthInterpolationProps = Pick<DimensionProps, 'width'>;

export type BaseProps = WidthInterpolationProps & {
  /**
   * children - One or more MenuItems, dividers, or Menu groups
   */
  children?: ComponentChildren;

  /**
   * The icon is the button's icon.  Note that this is not the menu
   * expand icon shown on the right (or to the left in right to left mode).
   */
  icon?: ComponentChild;

  /**
   * Specifies that the button element should be disabled.
   */
  isDisabled?: boolean;

  /**
   * Specifies if only the icon is shown, and no down chevron.
   */
  isIconOnly?: boolean;

  /**
   * The ID of an element (or space separated IDs of multiple elements) that
   * describes the button.
   */
  'aria-describedby'?: string;

  /**
   * Specifies if the menu is open
   */
  isMenuOpen?: boolean;

  /**
   * Property that triggers a callback immediately when toggle happens and value of expanded property should be updated
   */
  onToggleMenu?: (details: MenuToggleDetail) => void;

  /**
   * Indicates in what states the button has chrome (background and border).
   *     "borderless"': "Borderless buttons are a more prominent variation. Borderless
   * buttons are useful for supplemental actions that require minimal emphasis.",
   *     "outlined"': "Outlined buttons are salient, but lighter weight than
   * solid buttons. Outlined buttons are useful for secondary actions.",
   *     "solid"': "Solid buttons stand out, and direct the user's attention to the
   *  most important actions in the UI."
   */
  variant?: 'borderless' | 'outlined' | 'solid';

  /**
   * The size indicates how large the button is rendered: "sm", "md" (default), "lg".
   */
  size?: 'sm' | 'md' | 'lg';
};

/**
 * Note that either an accessibleLabel or tooltip must be specified.
 */
type VariationAccessibleLabelProps =
  | {
      /**
       * accessibleLabel - the aria label
       */
      accessibleLabel: string;

      /**
       * tooltip - overrides the accessibleLabel for the tooltip
       */
      tooltip?: string;
    }
  | {
      accessibleLabel?: string;

      tooltip: string;
    };

/**
 * A IconMenuButton is a combined action and menu button.
 */
export const IconMenuButton = forwardRef(
  (
    {
      children,
      tooltip,
      accessibleLabel,
      variant = 'outlined',
      isDisabled = false,
      isMenuOpen = false,
      isIconOnly = false,
      onToggleMenu,
      size = 'md',
      width,
      icon,
      'aria-describedby': ariaDescribedBy
    }: BaseProps & VariationAccessibleLabelProps,
    ref?: ForwardedRef<HTMLButtonElement>
  ) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    useImperativeHandle(ref!, () => buttonRef.current!, [buttonRef]);

    const { triggerProps, menuProps } = useMenuAction({
      isDisabled: isDisabled,
      onToggleMenu: onToggleMenu,
      isMenuOpen: isMenuOpen,
      anchorRef: buttonRef
    });

    const { tooltipContent, tooltipProps } = useTooltip({
      text: tooltip ? tooltip : accessibleLabel,
      isDisabled: isMenuOpen
    });

    const rootButton = (
      <Fragment>
        <BaseButton
          {...mergeProps(tooltipProps, triggerProps)}
          styling={isMenuOpen ? ['min', 'active'] : ['min']}
          isDisabled={isDisabled}
          variant={variant}
          width={width}
          size={size}
          ref={buttonRef}
          aria-describedby={ariaDescribedBy}
          aria-label={accessibleLabel ?? tooltip}>
          <ButtonLayout
            display={'icons'}
            startIcon={icon}
            endIcon={isIconOnly ? null : <ChevronDownIcon />}
            size={size}></ButtonLayout>
          {tooltipContent}
        </BaseButton>
      </Fragment>
    );

    return isDisabled ? (
      rootButton
    ) : (
      <Fragment>
        {rootButton}
        <Menu {...menuProps}>{children}</Menu>
      </Fragment>
    );
  }
);
